import styled from 'styled-components';

import { Link } from 'components/Toolkit/Button/Link';
import { media } from 'helpers/breakpoints';
import { DetailList } from 'components/Toolkit/DetailList/DetailList';
import type { InfoListItemProps } from 'components/Toolkit/InfoListItem/InfoListItem';

export interface EditorialProps {
  title?: string;
  content: string;
  linkText?: string;
  link?: string;
  image?: string;
  sponsored?: boolean;
  list?: InfoListItemProps[]; // Note we need to build the list first outside this component and pass it in in the shape we desire. Prismic doesn't give us a nice list object to just map over
}

type Variant = 'Default' | 'Image' | 'List';

const Container = styled.div<{ variant?: Variant }>`
  display: flex;
  flex-direction: column;

  background-color: ${({ theme }) => theme.colors.WHITE};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  padding: ${({ theme, variant }) =>
    variant === 'Image' ? 0 : theme.spacing.M16};
  max-width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing.M16};

  ${media.large} {
    padding: ${({ theme, variant }) =>
      variant === 'Image' ? 0 : theme.spacing.M24};
    margin-bottom: ${({ theme }) => theme.spacing.M24};
  }
`;

const MainContent = styled.div<{ variant?: Variant }>`
  display: flex;
  flex-direction: ${({ variant }) => (variant === 'Image' ? 'column' : 'row')};

  height: 100%;
  position: relative;

  ${media.large} {
    flex-direction: row;
  }
`;

const TextContent = styled.div<{ variant?: Variant }>`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  justify-content: space-between;
  padding: ${({ theme, variant }) =>
    variant === 'Image' ? `${theme.spacing.M16} ${theme.spacing.S8}` : 0};

  ${media.large} {
    padding: ${({ theme, variant }) =>
      variant === 'Image' ? `${theme.spacing.M24} ${theme.spacing.M16}` : 0};
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;

  ${media.large} {
    width: 320px;
  }
`;

const Picture = styled.picture`
  img {
    width: 100%;
    max-width: 100%;
    height: auto;
    border-radius: ${({ theme }) =>
      `${theme.borderRadius.default} 0 0 ${theme.borderRadius.default}`};
    ${media.large} {
      width: 320px;
      max-width: 320px;
    }
  }
`;

const Title = styled.h3`
  ${({ theme }) => theme.fontSize.M16};
  color: ${({ theme }) => theme.colors.GREY_DARKER};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  margin-bottom: ${({ theme }) => theme.spacing.S8};

  ${media.large} {
    ${({ theme }) => theme.fontSize.L18};
  }
`;

const EditorialContainer = styled.div<{ variant?: Variant }>`
  background-color: ${({ theme }) => theme.colors.WHITE};
  border-radius: ${({ theme }) => theme.borderRadius.default};

  padding: ${({ theme, variant }) =>
    variant === 'Image' ? 0 : theme.spacing.S4};

  display: flex;
  flex: 1 1 auto;
  flex-direction: column;

  ${media.large} {
    padding: ${({ variant }) => (variant === 'Image' ? 0 : 0)};
  }
`;

const Paragraph = styled.p`
  ${({ theme }) => theme.fontSize.M14};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

const Sponsored = styled.span`
  ${({ theme }) => theme.fontSize.B12};
  background: rgba(0, 0, 0, 0.48);
  border-radius: ${({ theme }) => theme.borderRadius.default};
  color: ${({ theme }) => theme.colors.WHITE};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  padding: ${({ theme }) => theme.spacing.S4};
  margin: ${({ theme }) => theme.spacing.S8};

  position: absolute;
  bottom: 0;
  left: 0;
`;

const SDetailList = styled(DetailList)<{ variant?: Variant }>`
  display: ${({ variant }) => (variant === 'List' ? 'grid' : 'none')};
  margin-bottom: ${({ theme }) => theme.spacing.M16};

  img {
    max-width: 16px;
  }
`;

const SLink = styled(Link)`
  ${({ theme }) => theme.fontSize.M14};
  color: ${({ theme }) => theme.colors.BLUE};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  text-transform: capitalize;
`;

function Editorial({
  title,
  content,
  linkText,
  link,
  image,
  sponsored,
  list,
}: EditorialProps) {
  let variant: Variant = 'Default';

  const isImage = Boolean(image);
  const isList = Boolean(list && !isImage);

  if (isImage) {
    variant = 'Image';
  }

  if (isList) {
    variant = 'List';
  }

  return (
    <Container
      variant={variant}
      className={isImage ? 'image' : isList ? 'list' : 'default'}
    >
      <MainContent variant={variant}>
        {image && (
          <ImageContainer>
            <Picture>
              <img src={image} alt={title} width="600" height="450" />
            </Picture>
            {sponsored && <Sponsored>Sponsored</Sponsored>}
          </ImageContainer>
        )}
        <TextContent variant={variant}>
          <EditorialContainer variant={variant}>
            {title && <Title>{title}</Title>}
            <Paragraph>{content}</Paragraph>
          </EditorialContainer>
          {list && <SDetailList list={list} variant={variant} />}
          {linkText && (
            <SLink href={link} target="_blank">
              {linkText}
            </SLink>
          )}
        </TextContent>
      </MainContent>
    </Container>
  );
}

export { Editorial };
